import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ShoppingBag, Play } from "lucide-react";
import { AmbientVideo } from "@/components/site/AmbientVideo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

function pickFeatured(list) {
  // Exclude Eli Baker from the top feature (still shown in the Stories grid below).
  const pool = (list || []).filter((s) => !(s.title || "").toLowerCase().includes("eli baker"));
  if (pool.length === 0) return null;
  const pinned = pool.filter((s) => s.pinned).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  if (pinned.length) return pinned[0];
  const byShares = [...pool].sort((a, b) => (b.share_clicks || 0) - (a.share_clicks || 0));
  if ((byShares[0].share_clicks || 0) > 0) return byShares[0];
  return [...pool].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
}

export const FeaturedStory = () => {
  const [story, setStory] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/stories`)
      .then((r) => active && setStory(pickFeatured(r.data)))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!story) return null;

  const trending = (story.share_clicks || 0) > 0;
  const videoId = (story.video_url || "").match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/)?.[1] || "";

  return (
    <section id="top-story" data-testid="featured-story" className="px-5 md:px-10 py-16 md:py-24 border-t border-white/10">
      <div className="flex items-center gap-3 mb-8 md:mb-10">
        <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold text-[#E30613]">
          {videoId ? "New Video Feature" : trending ? "Trending Now" : "The Feature"}
        </span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <Link to={`/story/${story.id}`} data-testid="featured-read-link" className="group block">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative overflow-hidden"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-black">
              {playing && videoId ? (
                <iframe
                  data-testid="featured-video"
                  title={story.title}
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <>
                  <img
                    src={mediaUrl(story.image)}
                    alt={story.title}
                    className="w-full h-full object-cover grayscale-img group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
                  />
                  {videoId && <AmbientVideo videoId={videoId} start={8} seconds={5} />}
                  {videoId && <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />}
                </>
              )}
              {videoId && !playing && (
                <button
                  type="button"
                  data-testid="featured-play"
                  aria-label="Play video"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPlaying(true); }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#E30613] text-white shadow-[0_0_60px_rgba(227,6,19,0.45)] transition-transform duration-300 hover:scale-105">
                    <span className="absolute inset-0 rounded-full border border-white/40 animate-ping opacity-40" />
                    <Play size={30} fill="#fff" className="translate-x-[2px]" />
                  </span>
                  <span className="absolute bottom-4 left-4 bg-black/80 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white">Watch the interview</span>
                </button>
              )}
              <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] border-r border-b border-white/10">
                {story.network}{videoId ? " · Video" : ""}
              </span>
              {story.series && (
                <span className="absolute bottom-0 right-0 bg-[#E30613] px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-white">
                  {story.series}{story.part ? ` · Pt ${story.part}` : ""}
                </span>
              )}
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mb-4">{story.category}</p>
            <h2
              data-testid="featured-title"
              className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-6xl text-white group-hover:text-[#E30613] transition-colors duration-300"
            >
              {story.title}
            </h2>
            <p className="text-base text-neutral-400 mt-6 leading-relaxed max-w-xl">{story.excerpt}</p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-bold text-white">
                Read The Feature
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#E30613] text-[#E30613] group-hover:bg-[#E30613] group-hover:text-white transition-colors duration-300">
                  <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
              {story.product_url && (
                <span
                  role="link"
                  tabIndex={0}
                  data-testid="featured-shop"
                  data-href={story.product_url}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(story.product_url, "_blank", "noopener,noreferrer"); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); window.open(story.product_url, "_blank", "noopener,noreferrer"); } }}
                  className="inline-flex items-center gap-2 bg-[#E30613] text-white text-xs uppercase tracking-[0.25em] font-bold px-6 py-3.5 hover:bg-white hover:text-black transition-colors duration-300 cursor-pointer"
                >
                  <ShoppingBag size={15} /> {story.product_label || "Shop The Drop"}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </Link>
    </section>
  );
};
